/**
 * REST client (계획서 §12).
 *
 * - 인증은 server-side opaque session cookie 를 쓰므로 항상 `credentials: "include"` 다.
 * - 오류는 application/problem+json 이므로 `ApiError` 로 감싼다.
 * - 변경 API 는 same-origin 이어야 하므로 절대 URL 을 만들지 않는다.
 */

import type {
  AgentsResponse,
  AuditItem,
  CurrentUser,
  HealthReadyResponse,
  HumanStartResponse,
  OntologyResponse,
  ProblemDetail,
  RunSnapshot,
  StrategyCode,
  WorkflowResponse,
} from "@/types/api";

export const API_PREFIX = "/api/v1";

/** problem+json 을 담은 오류. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly problem: ProblemDetail | null;

  constructor(status: number, code: string, message: string, problem: ProblemDetail | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.problem = problem;
  }

  /** 오래된 plan_version 으로 인한 충돌인가 (§9.2). */
  get isStalePlanVersion(): boolean {
    return this.status === 409 && this.code === "STALE_PLAN_VERSION";
  }
}

async function readProblem(response: Response): Promise<ProblemDetail | null> {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("json")) return null;
  try {
    return (await response.json()) as ProblemDetail;
  } catch {
    return null;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

/** `/api/v1` 아래의 endpoint 를 호출한다. */
export async function apiFetch<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const { method = "GET", body, signal } = options;

  const init: RequestInit = {
    method,
    credentials: "include",
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  };

  const response = await fetch(`${API_PREFIX}${path}`, init);

  if (!response.ok) {
    const problem = await readProblem(response);
    throw new ApiError(
      response.status,
      problem?.code ?? `HTTP_${String(response.status)}`,
      problem?.title ?? response.statusText,
      problem,
    );
  }

  if (response.status === 204) return null as TResponse;
  return (await response.json()) as TResponse;
}

// --------------------------------------------------------------------------- //
// 인증 (§12.1)
// --------------------------------------------------------------------------- //
export function login(username: string, password: string): Promise<CurrentUser> {
  return apiFetch<CurrentUser>("/auth/login", {
    method: "POST",
    body: { username, password },
  });
}

export async function logout(): Promise<void> {
  await apiFetch<null>("/auth/logout", { method: "POST" });
}

export function fetchCurrentUser(): Promise<CurrentUser> {
  return apiFetch<CurrentUser>("/auth/me");
}

export function fetchReadiness(): Promise<HealthReadyResponse> {
  return apiFetch<HealthReadyResponse>("/health/ready");
}

// --------------------------------------------------------------------------- //
// 실행·시계 (§12.2)
// --------------------------------------------------------------------------- //
export function fetchCurrentRun(): Promise<RunSnapshot> {
  return apiFetch<RunSnapshot>("/runs/current");
}

export function fetchRunState(runId: string): Promise<RunSnapshot> {
  return apiFetch<RunSnapshot>(`/runs/${runId}/state`);
}

export function resetRun(): Promise<RunSnapshot> {
  return apiFetch<RunSnapshot>("/runs/reset", { method: "POST", body: {} });
}

export type ClockAction =
  | { action: "play" }
  | { action: "pause" }
  | { action: "speed"; speed_multiplier: number }
  | { action: "seek"; virtual_at: string };

export function controlClock(runId: string, request: ClockAction): Promise<RunSnapshot> {
  return apiFetch<RunSnapshot>(`/runs/${runId}/clock`, { method: "POST", body: request });
}

export function raiseIncident(runId: string, repairMinutes?: number): Promise<RunSnapshot> {
  return apiFetch<RunSnapshot>(`/runs/${runId}/incidents`, {
    method: "POST",
    body: repairMinutes === undefined ? {} : { repair_minutes: repairMinutes },
  });
}

export function requestEarlyCompletion(incidentId: string): Promise<RunSnapshot> {
  return apiFetch<RunSnapshot>(`/incidents/${incidentId}/early-complete`, {
    method: "POST",
    body: {},
  });
}

export function confirmRisk(incidentId: string, confirmed: boolean): Promise<RunSnapshot> {
  return apiFetch<RunSnapshot>(`/incidents/${incidentId}/risk-confirmation`, {
    method: "POST",
    body: { confirmed },
  });
}

// --------------------------------------------------------------------------- //
// 계획과 승인 (§12.3)
// --------------------------------------------------------------------------- //
export function decidePlan(
  planVersion: number,
  decision: "approve" | "reject",
  strategyCode?: StrategyCode,
): Promise<RunSnapshot> {
  return apiFetch<RunSnapshot>("/plans/current/decision", {
    method: "POST",
    body: {
      plan_version: planVersion,
      decision,
      ...(strategyCode ? { strategy_code: strategyCode } : {}),
    },
  });
}

export function controlApprovalTimer(
  planVersion: number,
  action: "hold" | "resume",
): Promise<RunSnapshot> {
  return apiFetch<RunSnapshot>("/plans/current/approval-timer", {
    method: "POST",
    body: { plan_version: planVersion, action },
  });
}

/** 인간 가동. double click 은 같은 message_id 를 돌려준다 (§12.5). */
export function humanStart(planVersion: number): Promise<HumanStartResponse> {
  return apiFetch<HumanStartResponse>("/plans/current/human-start", {
    method: "POST",
    body: { plan_version: planVersion },
  });
}

// --------------------------------------------------------------------------- //
// 관찰 (§12.4)
// --------------------------------------------------------------------------- //
export function fetchAgents(runId: string): Promise<AgentsResponse> {
  return apiFetch<AgentsResponse>(`/runs/${runId}/agents`);
}

export function fetchOntology(runId: string): Promise<OntologyResponse> {
  return apiFetch<OntologyResponse>(`/runs/${runId}/ontology`);
}

export function fetchAudit(runId: string): Promise<{ items: AuditItem[] }> {
  return apiFetch<{ items: AuditItem[] }>(`/runs/${runId}/audit`);
}

export function fetchWorkflow(runId: string): Promise<WorkflowResponse> {
  return apiFetch<WorkflowResponse>(`/runs/${runId}/workflow`);
}
